import React, { useMemo, useState } from "react";
import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, MenuItem, TextField } from "@mui/material";
import { hd, errorMessage, errorDetails } from "./api";
import { useHd } from "./HelpdeskContext";
import { CustomFieldInputs } from "./CustomFieldInputs";
import { primaryButtonSx, ghostButtonSx } from "../user/complaints/shared";

const empty = { subject: "", description: "", name: "", email: "", phone: "", priorityId: "", categoryId: "", subcategoryId: "", teamId: "", assigneeId: "", tags: "", sendAcknowledgement: true, customFields: {} };

export default function NewTicketDialog({ open, onClose, onCreated, defaults = {} }) {
  const ctx = useHd();
  const [f, setF] = useState({ ...empty, ...defaults });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));

  const cats = useMemo(() => ctx.categories.filter((c) => c.isActive && c.level === "category"), [ctx.categories]);
  const subs = useMemo(() => (f.categoryId ? ctx.categories.filter((c) => c.isActive && c.parentId && String(c.parentId) === String(f.categoryId)) : []), [ctx.categories, f.categoryId]);
  const ticketFields = ctx.customFields.filter((c) => c.entity === "ticket");

  const submit = async () => {
    setError("");
    setFieldErrors({});
    if (!f.subject.trim()) return setError("Subject is required");
    if (!f.email.trim() && !f.phone.trim() && !defaults.contactId) return setError("Enter the customer's email or phone");
    setSaving(true);
    try {
      const t = await hd.createTicket({
        subject: f.subject, description: f.description, channel: defaults.channel || "web",
        contactId: defaults.contactId, requester: defaults.contactId ? undefined : { name: f.name, email: f.email || undefined, phone: f.phone || undefined },
        priorityId: f.priorityId || undefined, categoryId: f.categoryId || undefined, subcategoryId: f.subcategoryId || undefined,
        teamId: f.teamId || undefined, assigneeId: f.assigneeId || undefined,
        tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean), customFields: f.customFields, sendAcknowledgement: f.sendAcknowledgement,
      });
      ctx.notify(`Ticket ${t.key} created`);
      setF({ ...empty, ...defaults });
      onCreated?.(t);
    } catch (err) {
      setError(errorMessage(err));
      setFieldErrors(errorDetails(err) || {});
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>New ticket</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12}><TextField label="Subject" fullWidth required value={f.subject} onChange={set("subject")} inputProps={{ maxLength: 500 }} /></Grid>
          <Grid item xs={12}><TextField label="Description" fullWidth multiline minRows={3} value={f.description} onChange={set("description")} /></Grid>
          {!defaults.contactId && (
            <>
              <Grid item xs={12} sm={4}><TextField label="Customer name" fullWidth value={f.name} onChange={set("name")} /></Grid>
              <Grid item xs={12} sm={4}><TextField label="Customer email" type="email" fullWidth value={f.email} onChange={set("email")} /></Grid>
              <Grid item xs={12} sm={4}><TextField label="Customer phone" fullWidth value={f.phone} onChange={set("phone")} /></Grid>
            </>
          )}
          <Grid item xs={12} sm={4}>
            <TextField select label="Priority" fullWidth value={f.priorityId} onChange={set("priorityId")}>
              <MenuItem value="">Default</MenuItem>
              {ctx.priorities.filter((p) => p.isActive).map((p) => <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select label="Category" fullWidth value={f.categoryId} onChange={(e) => setF((p) => ({ ...p, categoryId: e.target.value, subcategoryId: "" }))}>
              <MenuItem value="">None</MenuItem>
              {cats.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select label="Subcategory" fullWidth value={f.subcategoryId} onChange={set("subcategoryId")} disabled={!subs.length}>
              <MenuItem value="">None</MenuItem>
              {subs.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select label="Team" fullWidth value={f.teamId} onChange={set("teamId")}>
              <MenuItem value="">None</MenuItem>
              {ctx.teams.filter((t) => t.isActive).map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select label="Assignee" fullWidth value={f.assigneeId} onChange={set("assigneeId")} disabled={!ctx.can("tickets.assign")}>
              <MenuItem value="">Unassigned</MenuItem>
              {ctx.agents.map((a) => <MenuItem key={a.memberId} value={a.memberId}>{a.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}><TextField label="Tags (comma separated)" fullWidth value={f.tags} onChange={set("tags")} /></Grid>
          {ticketFields.length > 0 && (
            <Grid item xs={12}>
              <CustomFieldInputs fields={ticketFields} values={f.customFields} errors={fieldErrors} onChange={(v) => setF((p) => ({ ...p, customFields: v }))} />
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControlLabel control={<Checkbox checked={f.sendAcknowledgement} onChange={set("sendAcknowledgement")} />} label="Email the customer an acknowledgement" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" sx={ghostButtonSx} onClick={onClose}>Cancel</Button>
        <Button variant="contained" sx={primaryButtonSx} onClick={submit} disabled={saving}>{saving ? "Creating…" : "Create ticket"}</Button>
      </DialogActions>
    </Dialog>
  );
}
